import React, {
  ReactElement,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import classnames from 'classnames/bind';
import styles from './rtbCampaignInfo.module.scss';
import i18n from 'i18n';
import { AlertDialog } from 'components/AlertDialog';
import { FormikField } from 'components/form/field/FormikField';
import { LabelField } from 'components/form/field/LabelField';
import { Form, Row, Col } from 'react-bootstrap';
import { numberWithCommas } from 'utils/StringUtil';
import { useFormikContext } from 'formik';
import moment from 'moment';

const cx = classnames.bind(styles);

export type CampaignInfoState = {
  alertContent?: string | ReactElement | ReactElement[];
};

const RtbCampaignInfo: React.FC<any> = (props) => {
  const {
    formType,
    orderName,
    minDate,
    maxDate,
    isCampaignStart,
    products,
    autoProducts,
    selectPattern,
    runningPattern,
    defaultBudgetType,
    roasStrategy,
    spents,
  } = props;
  const dateRangePickerClassName = cx('componentWithTips', {
    disableStart: isCampaignStart,
  });
  const { setFieldValue, values } = useFormikContext<any>();
  const [alertContent, setAlertContent] = useState<
    string | ReactElement | ReactElement[] | undefined
  >(undefined);
  const productType = useCallback(
    () => (selectPattern === 'AUTO' ? autoProducts : products),
    [autoProducts, products, selectPattern],
  );
  const defaultDailyBudget = useMemo(() => {
    return values.dailyTargetBudget;
  }, []);

  const defaultBudget = useMemo(() => {
    return values.budget;
  }, []);

  // 走期類型
  const [dateRange, setDateRange] = useState<string>(
    runningPattern !== null ? runningPattern : 'UNLIMITED',
  );
  // 停用日期選擇
  const [disableDateRange, setDisableDateRange] = useState('');
  // 活動走期天數
  const [diffDays, setDiffDays] = useState(1);
  // 預算類型
  const [budgetType, setBudgetType] = useState(
    defaultBudgetType ? defaultBudgetType : 'DAILY',
  );
  const [showSuggestedDailyBudget, setSuggestedDailyBudget] =
    useState<boolean>(false);
  // 切換預算類型
  const changeBudgetType = (event) => {
    setBudgetType(event.target.value);
    setFieldValue('budgetType', event.target.value);
    setFieldValue('dailyTargetBudget', 0);
    setFieldValue('budget', 0);
  };
  // 切換走期類型
  const changeDateRange = (event) => {
    setDateRange(event.target.value);
    setFieldValue('runningPattern', event.target.value);
    setFieldValue('dailyTargetBudget', 0);
    if (values.dailyTargetBudget !== defaultDailyBudget) {
      checkIsUnderBudget();
    }
    if (values.budget !== defaultBudget) {
      checkIsUnderBudget();
    }
  };

  // ROAS選擇
  const [roas, setRoas] = useState(roasStrategy ? roasStrategy : 'HIGH_ROAS');
  const changeROAS = (event) => {
    setRoas(event.target.value);
    setFieldValue('roasStrategy', event.target.value);
  };

  const checkIsUnderBudget = () => {
    if (budgetType === 'DAILY') {
      autoSetBudget();
    } else {
      autoSetDailyBudget();
    }
  };

  const dismissAlertWindow = () => {
    setAlertContent(undefined);
  };
  // 日預算
  const renderDailyBudgetPlan = () => {
    const { currency } = props;
    const inputProps = {
      prefix: currency,
      name: 'dailyTargetBudget',
      type: 'number',
      min: 10,
      autoComplete: 'off',
      onchange: checkIsUnderBudget,
      fieldContentWidth: 184,
      formGroupClassName: styles.marginBottom0,
    };

    return <FormikField.InputGroup {...inputProps} />;
  };
  // 總預算
  const renderTargetBudgetInput = () => {
    const { currency } = props;
    const inputProps = {
      prefix: currency,
      name: 'budget',
      type: 'number',
      min: 10,
      autoComplete: 'off',
      onchange: checkIsUnderBudget,
      fieldContentWidth: 184,
      formGroupClassName: styles.marginBottom0,
    };

    return <FormikField.InputGroup {...inputProps} />;
  };
  // 無限走期建議日預算
  const suggestedDailyBudget = () => {
    const dateRange = runningPattern === 'UNLIMITED' ? 14 : diffDays;
    return `根據商品設定與走期，建議日預算 > ${numberWithCommas(
      Math.ceil((productType().length * 2500) / dateRange),
    )}`;
  };

  // 建議總預算
  const SuggestedTotalBudget = () => {
    return numberWithCommas(Math.ceil(productType().length * 2500));
  };
  // 限制建議日預算
  const limitSuggestedDailyBudget = () => {
    // const { budget } = props;
    return numberWithCommas(
      (Number(values.budget) - Number(spents)).toFixed(2),
    );
  };
  // 限制建議總預算
  const limitSuggestedTotalBudget = () => {
    // const { dailyBudget } = props;
    let start = new Date(values.startDate);
    let end = new Date(values.endDate);
    let currentDay = new Date();
    const createDiffTime = Math.abs(end.getTime() - start.getTime());
    const createDiffDays = Math.ceil(createDiffTime / (1000 * 60 * 60 * 24));
    const editDiffTime =
      start.getTime() > currentDay.getTime()
        ? Math.abs(end.getTime() - start.getTime())
        : Math.abs(end.getTime() - currentDay.getTime());
    const editDiffDays = Math.ceil(editDiffTime / (1000 * 60 * 60 * 24));
    switch (formType) {
      case 'create':
        return numberWithCommas(
          (Number(values.dailyTargetBudget) * createDiffDays).toFixed(2),
        );
      case 'edit':
        return numberWithCommas(
          (
            Number(Number(spents).toFixed(2)) +
            Number(values.dailyTargetBudget) * editDiffDays
          ).toFixed(2),
        );
      case 'copy':
        return numberWithCommas(
          (
            Number(Number(spents).toFixed(2)) +
            Number(values.dailyTargetBudget) * editDiffDays
          ).toFixed(2),
        );
      default:
        break;
    }
  };
  // 自動設定總預算
  const autoSetBudget = () => {
    if (dateRange === 'LIMITED') {
      let start = new Date(values.startDate);
      let end = new Date(values.endDate);
      let currentDay = new Date();
      const createDiffTime = Math.abs(end.getTime() - start.getTime());
      const createDiffDays = Math.ceil(createDiffTime / (1000 * 60 * 60 * 24));
      const editDiffTime =
        start.getTime() > currentDay.getTime()
          ? Math.abs(end.getTime() - start.getTime())
          : Math.abs(end.getTime() - currentDay.getTime());
      const editDiffDays = Math.ceil(editDiffTime / (1000 * 60 * 60 * 24));
      if (budgetType === 'DAILY') {
        switch (formType) {
          case 'create':
            setFieldValue(
              'budget',
              (Number(values.dailyTargetBudget) * createDiffDays).toFixed(2),
            );
            setSuggestedDailyBudget(true);
            break;
          case 'edit':
            setFieldValue(
              'budget',
              (
                Number(Number(spents).toFixed(2)) +
                Number(values.dailyTargetBudget) * editDiffDays
              ).toFixed(2),
            );
            setSuggestedDailyBudget(true);
            break;
          case 'copy':
            setFieldValue(
              'budget',
              (
                Number(Number(spents).toFixed(2)) +
                Number(values.dailyTargetBudget) * editDiffDays
              ).toFixed(2),
            );
            setSuggestedDailyBudget(true);
            break;
          default:
            break;
        }
      }
    }
    if (dateRange === 'UNLIMITED') {
      setFieldValue(
        'budget',
        (
          Number(Number(spents).toFixed(2)) + Number(values.dailyTargetBudget)
        ).toFixed(2),
      );
      setSuggestedDailyBudget(true);
    }
  };
  // 自動設定日預算
  const autoSetDailyBudget = () => {
    if (dateRange === 'LIMITED') {
      const { budget } = props;
      if (budgetType === 'TOTAL') {
        setFieldValue('dailyTargetBudget', Number(budget).toFixed(2));
      }
    }
  };

  useEffect(() => {
    if (formType === 'create') {
      if (!roasStrategy) setFieldValue('roasStrategy', 'HIGH_ROAS');
      if (!runningPattern) setFieldValue('runningPattern', 'UNLIMITED');
    }
    if (formType === 'edit') {
      if (runningPattern === 'UNLIMITED') {
        setDisableDateRange('setDateRange');
      }
    }
    suggestedDailyBudget();
  }, []);

  useEffect(() => {
    suggestedDailyBudget();
  }, [autoProducts, products]);

  useEffect(() => {
    if (values.dailyTargetBudget !== defaultDailyBudget) {
      checkIsUnderBudget();
    }
    if (values.budget !== defaultBudget) {
      checkIsUnderBudget();
    }
    if (values.dailyTargetBudget === 0) {
      setSuggestedDailyBudget(false);
    }
  }, [values.dailyTargetBudget, values.budget]);

  useEffect(() => {
    const start = new Date(values.startDate);
    const end = new Date(values.endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setDiffDays(diffDays);
  }, [values.startDate, values.endDate]);

  useEffect(() => {
    // 監控走期變更重新設定預算
    if (dateRange === 'UNLIMITED') {
      setBudgetType('DAILY');
      setFieldValue('budgetType', 'DAILY');
    }
  }, [dateRange]);

  useEffect(() => {
    if (dateRange === 'UNLIMITED') {
      const ignoreTimeZoneFormat = 'YYYY-MM-DD HH:mm:ss';
      setFieldValue(
        'startDate',
        moment(new Date()).startOf('hour').format(ignoreTimeZoneFormat),
      );
    }
  }, [runningPattern, disableDateRange]);

  useEffect(() => {
    if (
      values.dailyTargetBudget !== defaultDailyBudget ||
      values.budget !== defaultBudget
    ) {
      checkIsUnderBudget();
    }
  }, [budgetType]);

  useEffect(() => {
    suggestedDailyBudget();
    limitSuggestedTotalBudget();
    limitSuggestedDailyBudget();
  }, [diffDays]);

  return (
    <fieldset>
      <legend>
        <span>{i18n.t<string>('campaignInfo.labels.title')}</span>
      </legend>
      <div className={styles.component}>
        {alertContent && (
          <AlertDialog
            title={i18n.t<string>('common.warning')}
            message={alertContent}
            danger={false}
            dismiss={dismissAlertWindow}
          />
        )}
        <LabelField
          label={i18n.t<string>('campaignInfo.labels.order')}
          name='orderName'
          value={orderName}
          labelColSm={2}
          inputColSm={4}
        />
        <FormikField.Input
          label={i18n.t<string>('rtbCampaign.base.campaignName')}
          name='name'
          labelColSm={2}
          inputColSm={4}
        />
        <Form.Group as={Row} controlId={'dateRange'}>
          <Col sm={2}>
            <Form.Label className={'form-label col-form-label col'}>
              {i18n.t<string>('rtbCampaign.base.dateRange')}
            </Form.Label>
          </Col>
          <Col sm={7} className={styles.gridRows}>
            <div className={styles.optRadio}>
              <Form.Check
                type={'radio'}
                label={i18n.t<string>('rtbCampaign.base.infinitely')}
                name={runningPattern}
                value={'UNLIMITED'}
                id={'UNLIMITED'}
                style={{ paddingLeft: 0 }}
                onChange={changeDateRange}
                checked={dateRange === 'UNLIMITED'}
                disabled={false}
              />
              <Form.Text
                as={'span'}
                className={styles.description}
                style={{ paddingLeft: '1.5rem' }}
                muted
                hidden={dateRange !== 'UNLIMITED'}
              >
                廣告會投放至廣告群組預算用完為止
              </Form.Text>
            </div>
            <div className={styles.optRadio}>
              <div className={styles.flexColumns}>
                <Form.Check
                  type={'radio'}
                  label={i18n.t<string>('rtbCampaign.base.setDateRange')}
                  value={'LIMITED'}
                  id={'LIMITED'}
                  style={{ paddingLeft: 0 }}
                  onChange={changeDateRange}
                  checked={dateRange === 'LIMITED'}
                  disabled={disableDateRange === 'setDateRange'}
                />
                {dateRange === 'LIMITED' && (
                  <FormikField.DateRangePicker
                    minDate={minDate}
                    maxDate={maxDate}
                    startDateFormikName='startDate'
                    endDateFormikName='endDate'
                    name='dayRange'
                    showTimePicker={true}
                    startDatePickerDisabled={isCampaignStart}
                    onPopOverClosed={checkIsUnderBudget}
                    inputColSm={12}
                    formGroupClassName={dateRangePickerClassName}
                  />
                )}
              </div>
              <Form.Text
                as={'span'}
                className={styles.description}
                style={{ paddingLeft: '1.5rem' }}
                muted
                hidden={dateRange === 'UNLIMITED'}
              >
                建議活動至少大於兩周，以達到系統最佳學習效果
              </Form.Text>
              <Form.Text
                as={'span'}
                className={styles.description}
                style={{ paddingLeft: '1.5rem' }}
                muted
                hidden={dateRange === 'UNLIMITED' && diffDays !== 0}
              >
                走期共 {diffDays} 天
              </Form.Text>
            </div>
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId={'age-opt-preferred'}>
          <Col sm={2}>
            <Form.Label className={'form-label col-form-label col'}>
              {i18n.t<string>('rtbCampaign.base.targetBudget')}
            </Form.Label>
          </Col>
          <Col sm={7} className={styles.gridRows}>
            <div className={styles.optRadio}>
              <div className={styles.flexColumns}>
                <Form.Check
                  type={'radio'}
                  label={i18n.t<string>('rtbCampaign.base.dailyTargetBudget')}
                  value={'DAILY'}
                  id={'DAILY'}
                  style={{ paddingLeft: 0 }}
                  onChange={changeBudgetType}
                  checked={budgetType === 'DAILY'}
                  disabled={false}
                />
                {budgetType === 'DAILY' && renderDailyBudgetPlan()}
              </div>
              <Form.Text
                as={'span'}
                className={styles.description}
                style={{ paddingLeft: '1.5rem' }}
                muted
                hidden={budgetType !== 'DAILY'}
              >
                {suggestedDailyBudget()}
              </Form.Text>
              {values.dailyTargetBudget > 0 && (
                <Form.Text
                  as={'span'}
                  className={styles.description}
                  style={{ paddingLeft: '1.5rem' }}
                  muted
                  hidden={
                    budgetType !== 'DAILY' ||
                    dateRange === 'UNLIMITED' ||
                    !showSuggestedDailyBudget
                  }
                >
                  您設定的總預算預估為 {limitSuggestedTotalBudget()}
                </Form.Text>
              )}
            </div>
            {dateRange !== 'UNLIMITED' && (
              <div className={styles.optRadio}>
                <div className={styles.flexColumns}>
                  <Form.Check
                    type={'radio'}
                    label={i18n.t<string>('rtbCampaign.base.totalTargetBudget')}
                    value={'TOTAL'}
                    id={'TOTAL'}
                    style={{ paddingLeft: 0 }}
                    onChange={changeBudgetType}
                    checked={budgetType === 'TOTAL'}
                    disabled={false}
                  />
                  {budgetType === 'TOTAL' && renderTargetBudgetInput()}
                </div>
                <Form.Text
                  as={'span'}
                  className={styles.description}
                  style={{ paddingLeft: '1.5rem' }}
                  muted
                  hidden={budgetType !== 'TOTAL'}
                >
                  根據商品設定，建議總預算 {'>'} {SuggestedTotalBudget()}
                  <br />
                  預算消耗速度依競爭情況浮動，可能於走期結束前消耗完畢
                </Form.Text>
              </div>
            )}
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId={'age-opt-preferred'}>
          <Col sm={2}>
            <Form.Label className={'form-label col-form-label col'}>
              {i18n.t<string>('rtbCampaign.base.bidStrategy')}
            </Form.Label>
          </Col>
          <Col sm={5}>
            <div className={styles['padding-top-7']}>
              <label>{i18n.t<string>('自訂投放策略')}</label>
              <p className={cx(styles.description, 'text-muted')}>
                {i18n.t<string>(
                  '系統根據您擬定的投放策略，自動出價與選字；請避免於兩周內更改，以優化至最佳結果',
                )}
              </p>
            </div>
            <div className={styles.selectROAS}>
              <div className={styles.optRadio}>
                <Form.Check
                  type={'radio'}
                  label={i18n.t<string>('rtbCampaign.base.highRoas')}
                  value={'HIGH_ROAS'}
                  id={'HIGH_ROAS'}
                  style={{ paddingLeft: 0 }}
                  onChange={changeROAS}
                  checked={roas === 'HIGH_ROAS'}
                  disabled={false}
                />
              </div>
              <div className={styles.optRadio}>
                <Form.Check
                  type={'radio'}
                  label={i18n.t<string>('rtbCampaign.base.mediumRoas')}
                  value={'MEDIUM_ROAS'}
                  id={'MEDIUM_ROAS'}
                  style={{ paddingLeft: 0 }}
                  onChange={changeROAS}
                  checked={roas === 'MEDIUM_ROAS'}
                  disabled={false}
                />
              </div>
              <div className={styles.optRadio}>
                <Form.Check
                  type={'radio'}
                  label={i18n.t<string>('rtbCampaign.base.lowRoas')}
                  value={'LOW_ROAS'}
                  id={'LOW_ROAS'}
                  style={{ paddingLeft: 0 }}
                  onChange={changeROAS}
                  checked={roas === 'LOW_ROAS'}
                  disabled={false}
                />
              </div>
            </div>
          </Col>
        </Form.Group>
      </div>
    </fieldset>
  );
};
export default RtbCampaignInfo;
